import { Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

import { marker as bitfToTranslate } from '@biesbjerg/ngx-translate-extract-marker';

import { Archive } from '@common/core/models';
import { ApiCallStateService, ArchivesService, LoaderService, StoreService } from '@common/core/services';
import { EBitfCloseEventStatus, EBitfUiMessageType, eStoreActions } from '@enums';
import {
  IBitfApiResponse,
  IBitfCloseEvent,
  IBitfGraphQlRequest,
  IBitfGraphQlResponse,
  IBulkUpdateItemsDialogCloseEvent,
  IBulkUpdateItemsDialogData,
} from '@common/interfaces';
import { ToastMessagesService } from '@web/core/services';
import { DynamicItemDataFormComponent } from '../dynamic-item-data-form/dynamic-item-data-form.component';
import { throwError } from 'rxjs';

@Component({
  selector: 'mpa-bulk-update-items-dialog',
  templateUrl: './bulk-update-items-dialog.component.html',
  styleUrls: ['./bulk-update-items-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BulkUpdateItemsDialogComponent implements OnInit {
  @ViewChild(DynamicItemDataFormComponent) itemDataform: DynamicItemDataFormComponent;

  form: UntypedFormGroup;
  concatChanges = false;
  constructor(
    public dialogRef: MatDialogRef<BulkUpdateItemsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: IBulkUpdateItemsDialogData,
    private apiCallStateService: ApiCallStateService,
    private archivesService: ArchivesService,
    private storeService: StoreService,
    private loaderService: LoaderService,
    private toastMessagesService: ToastMessagesService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.dialogData.bulkItemsIds?.length === 1) {
      this.loadData();
    }
  }

  onSave() {
    this.saveData();
  }

  onSaveAndPrint() {
    this.saveData({ shouldPrintArchiveItems: true });
  }

  onClose() {
    this.dialogRef.close({
      status: EBitfCloseEventStatus.CANCEL,
    } as IBitfCloseEvent<void>);
  }

  private loadData() {
    this.archivesService
      .getItemById({ variables: { id: this.dialogData.bulkItemsIds[0] } })
      .subscribe((response: IBitfGraphQlResponse<Archive>) => {
        this.storeService.setSelectedItem(response.content);
      });
  }

  private saveData(options?: { shouldPrintArchiveItems?: boolean }) {
    const bulkArchive: Partial<Archive> = Archive.getArchiveForBulkUpdate(this.itemDataform.form.value);

    if (Object.keys(bulkArchive).length === 0) {
      this.toastMessagesService.show({
        title: this.translateService.instant(bitfToTranslate('ITEM.TOAST.NOTHING_TO_UPDATE')),
        type: EBitfUiMessageType.INFO,
      });
      return;
    }

    this.loaderService.show();
    this.archivesService
      .bulkUpdateItems({
        ids: this.dialogData.bulkItemsIds,
        partialArchive: bulkArchive,
        concatChanges: this.concatChanges,
      })
      .pipe(
        switchMap((response: IBitfApiResponse<Archive[]>) => {
          const requestParams: IBitfGraphQlRequest = this.apiCallStateService.getApiRequest(
            this.dialogData.apiCallStateName
          );
          return this.archivesService
            .getAllIds({
              ...requestParams,
              page: 0,
              size: this.dialogData.bulkItemsIds.length,
              filter: [...requestParams.filter, { and: { id: { in: this.dialogData.bulkItemsIds } } }],
            })
            .pipe(
              map(idsResponse => ({
                updatedArchives: response.content,
                remainingIds: idsResponse.content.map(a => a.id),
              }))
            );
        }),
        map(({ updatedArchives, remainingIds }) => {
          const removedArchiveIds: number[] = [];

          this.dialogData.bulkItemsIds.forEach(id => {
            const updatedArchive = updatedArchives.find(archive => archive.id === id);
            if (remainingIds.includes(id) && updatedArchive) {
              this.storeService.updateGalleryItem({ id, newItem: updatedArchive });
            } else {
              removedArchiveIds.push(id);
            }
          });

          return removedArchiveIds;
        }),
        tap(removedArchiveIds => {
          this.storeService.setStore(() => {}, eStoreActions.EVENT_UPDATE_ARCHIVES_TOTAL_VALUE);

          this.toastMessagesService.show({
            title: this.translateService.instant(bitfToTranslate('ITEM.TOAST.ITEMS_UPDATED')),
            type: EBitfUiMessageType.SUCCESS,
          });

          this.dialogRef.close({
            status: EBitfCloseEventStatus.OK,
            data: {
              removedArchiveIds,
              shouldPrintArchiveItems: options?.shouldPrintArchiveItems,
            },
          } as IBitfCloseEvent<IBulkUpdateItemsDialogCloseEvent>);
        }),
        catchError(error => {
          this.toastMessagesService.show({
            title: this.translateService.instant(bitfToTranslate('ITEM.TOAST.ERROR_UPDATING_ITEMS')),
            type: EBitfUiMessageType.ERROR,
          });
          return throwError(error);
        }),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe();
  }
}
